import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import AnalysisReport, { ReportSkeleton } from '../components/AnalysisReport';
import ComparisonReport from '../components/ComparisonReport';
import BulkReport from '../components/BulkReport';
import MobileReport from '../components/MobileReport';
import ReportFooter from '../components/ReportFooter';
import PdfDownloadButton from '../components/PdfDownloadButton';
import { StrVsLtr } from '../components/StrAnalysis';
import { computeAnalysis, computeBulkAnalysis, optsForUnit, optsFromUnit, strOptsFor, unitHasStr, aedStr, RESALE_DEFAULTS } from '../lib/analysisEngine';
import { STR_CONFIG_FALLBACK, furnishingPreset } from '../lib/strConfig';
import { exportElementToPdf, exportMobileElementToPdf, pdfFilename } from '../lib/pdfExport';
import { waLink, CONTACT_NAME, CONTACT_COMPANY } from '../lib/config';
import { I18nProvider, makeT } from '../lib/i18n';
import AnalysisAssistant from '../components/AnalysisAssistant';
import { buildAnalysisContext } from '../lib/aiContext';

const LANG_KEY = 'affinity.analysis.lang.share';

const API = 'https://affinityconcept.tech/api';
const ILS_FALLBACK = 0.95;
const EUR_FALLBACK = 0.25;
const USD_FALLBACK = 0.27;

// WhatsApp brand glyph (inline so it needs no external asset).
function WhatsAppIcon({ className = 'w-5 h-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.615zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.767.967-.94 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
    </svg>
  );
}

// Update <title> + Open Graph tags so a WhatsApp/link preview reads professionally.
function setMeta(title, description) {
  if (typeof document === 'undefined') return;
  document.title = title;
  const set = (attr, key, val) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
    el.setAttribute('content', val);
  };
  set('property', 'og:title', title);
  set('property', 'og:description', description);
  set('property', 'og:type', 'website');
  set('name', 'twitter:title', title);
  set('name', 'twitter:description', description);
  set('name', 'description', description);
}

export default function ShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ils, setIls] = useState(ILS_FALLBACK);
  const [eur, setEur] = useState(EUR_FALLBACK);
  const [usd, setUsd] = useState(USD_FALLBACK);
  const [pdfBusy, setPdfBusy] = useState(false);
  const reportRef = useRef(null);
  const mobileRef = useRef(null);
  // Resale-cost toggle — initialised from the presenter's snapshot so the client
  // sees the numbers as prepared; they can still explore other states interactively.
  const [resale, setResale] = useState(RESALE_DEFAULTS);
  // "Ask about this deal" (single mode only, and only when the presenter enabled it) —
  // lets the chat drive the Exit Scenario slider, same controlled pattern as the internal page.
  const [chatExitYear, setChatExitYear] = useState(null);
  // Language (EN default). Remembered in localStorage; if the client never chose,
  // fall back to the language baked into the share snapshot (assumptions.lang).
  const langChosen = useRef(false);
  const [lang, setLangState] = useState(() => {
    try { const v = localStorage.getItem(LANG_KEY); if (v) { langChosen.current = true; return v; } } catch { /* ignore */ }
    return 'en';
  });
  const setLang = (v) => {
    langChosen.current = true;
    setLangState(v);
    try { localStorage.setItem(LANG_KEY, v); } catch { /* ignore */ }
  };
  const t = makeT(lang);
  const dir = lang === 'he' ? 'rtl' : 'ltr';
  // Client view defaults to Lite; the client can switch to Advanced. Remembered per session.
  const [view, setView] = useState(() => {
    try { return sessionStorage.getItem('affinity.analysis.view.share') || 'lite'; } catch { return 'lite'; }
  });
  useEffect(() => { try { sessionStorage.setItem('affinity.analysis.view.share', view); } catch { /* ignore */ } }, [view]);
  // EUR/USD conversion are optional on the share page — default OFF (AED + ₪ only),
  // remembered like the language choice.
  const [showEur, setShowEur] = useState(() => {
    try { return localStorage.getItem('affinity.analysis.showEur.share') === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('affinity.analysis.showEur.share', showEur ? '1' : '0'); } catch { /* ignore */ } }, [showEur]);
  const [showUsd, setShowUsd] = useState(() => {
    try { return localStorage.getItem('affinity.analysis.showUsd.share') === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('affinity.analysis.showUsd.share', showUsd ? '1' : '0'); } catch { /* ignore */ } }, [showUsd]);
  // Passed down instead of the raw rate: null hides every €/$ line across the report.
  const effEur = showEur ? eur : null;
  const effUsd = showUsd ? usd : null;
  useEffect(() => {
    const r = data?.assumptions?.resale;
    if (r) setResale({ enabled: !!r.enabled, commissionPct: r.commissionPct ?? 2, nocFee: r.nocFee ?? 5250 });
  }, [data]);
  // Honour the snapshot's default language only when the client hasn't picked one.
  useEffect(() => {
    const snapLang = data?.assumptions?.lang;
    if (!langChosen.current && (snapLang === 'he' || snapLang === 'en')) setLangState(snapLang);
  }, [data]);

  useEffect(() => {
    axios.get(`${API}/share/analysis/${token}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.response?.status === 410 ? 'expired' : 'unavailable'); setLoading(false); });
  }, [token]);

  const a = data?.assumptions || {};
  // Rate: prefer the snapshot taken at share time (client sees the numbers as prepared,
  // NOT the live rate). Fall back to a live fetch only for older shares without a snapshot.
  const rateSnap = a.rate || null;
  const fixedRate = (rateSnap && rateSnap.mode === 'manual' && Number(rateSnap.rate_used) > 0) ? Number(rateSnap.rate_used) : null;
  useEffect(() => {
    if (!data) return;
    // EUR/USD have no manual-override mode (unlike ILS) — always the live rate.
    const useLiveIls = !(rateSnap && Number(rateSnap.rate_used) > 0);
    if (!useLiveIls) setIls(Number(rateSnap.rate_used));
    axios.get('https://open.er-api.com/v6/latest/AED')
      .then(r => {
        const eurRate = r.data?.rates?.EUR; if (eurRate) setEur(eurRate);
        const usdRate = r.data?.rates?.USD; if (usdRate) setUsd(usdRate);
        if (useLiveIls) { const ilsRate = r.data?.rates?.ILS; if (ilsRate) setIls(ilsRate); }
      })
      .catch(() => { setEur(EUR_FALLBACK); setUsd(USD_FALLBACK); if (useLiveIls) setIls(ILS_FALLBACK); });
  }, [data]);   // eslint-disable-line

  // single
  const single = useMemo(() => {
    if (!data || data.mode !== 'single' || !data.items[0]) return null;
    const project = data.items[0].project;
    const unit = project.units.find(u => u.id === data.items[0].unit_id) || project.units[0];
    if (!unit) return null;
    const assumptions = {
      dldEnabled: a.dldEnabled ?? project.dld_enabled,
      dldRate: a.dldRate ?? 4,
      adminFees: a.adminFees ?? project.admin_fees ?? 0,
      scRate: a.scRate ?? unit.service_charge_rate ?? 14,
      startRent: a.startRent ?? unit.expected_rent_yearly_aed ?? 0,
      capBands: a.capBands || [8, 5, 3],
      rentBands: a.rentBands || [5, 3, 2],
      maturityYears: a.maturityYears || 10,
    };
    const strategy = a.strategy || 'ltr';
    const strOpts = { ...assumptions, strategy: 'str', furnishingCost: a.furnishingCost || 0, str: a.str || {} };
    return {
      project, unit, assumptions, strategy,
      engineOpts: strategy === 'ltr' ? assumptions : strOpts,
      analysis: computeAnalysis(project, unit, assumptions),
      // always computed (not just when strategy !== 'ltr') so "Ask about this deal" can
      // answer STR questions regardless of which strategy the presenter left it on.
      analysisStr: computeAnalysis(project, unit, strOpts),
    };
  }, [data]);   // eslint-disable-line

  // "Ask about this deal" — only offered when the presenter turned it on for this link
  // (data.allow_questions, single mode only). Context built from the SAME analysis
  // objects the report renders, using the frozen snapshot assumptions.
  const allowQuestions = !!(data?.allow_questions && single);
  const askContext = useMemo(
    () => single ? buildAnalysisContext({
      project: single.project, unit: single.unit, analysis: single.analysis, analysisStr: single.analysisStr,
      strategy: single.strategy, capBands: single.assumptions.capBands, rentBands: single.assumptions.rentBands,
      ils, rateMode: rateSnap?.mode || 'live',
    }) : null,
    [single, ils, rateSnap]
  );
  const askContextVersion = useMemo(() => JSON.stringify(askContext), [askContext]);

  // comparison — strategy is frozen from the snapshot (no live toggle on the public page)
  const compareEntries = useMemo(() => {
    if (!data || data.mode !== 'comparison') return [];
    const capBands = a.capBands || [8, 5, 3];
    const rentBands = a.rentBands || [5, 3, 2];
    const maturityYears = a.maturityYears || 10;
    const overrides = a.rentOverrides || {};
    // perUnit (default for new shares): each unit under its OWN saved assumptions, same
    // as the presenter's default comparison view. Older shares without the flag, or ones
    // explicitly created with the shared-override toggle, use the one snapshotted band set.
    const perUnit = !!a.perUnit;
    return data.items.map(it => {
      const project = it.project;
      const unit = project.units.find(u => u.id === it.unit_id) || project.units[0];
      if (!unit) return null;
      const ltrOpts = perUnit
        ? optsFromUnit(project, unit, overrides[unit.id])
        : optsForUnit(project, unit, capBands, rentBands, overrides[unit.id], maturityYears);
      const fc = (unit.furnishing_cost != null && unit.furnishing_cost !== '')
        ? unit.furnishing_cost : furnishingPreset(unit.unit_type, unit.is_fully_furnished, STR_CONFIG_FALLBACK);
      const strOpts = strOptsFor(ltrOpts, unit, fc);
      return {
        label: project.project_name, sub: `${unit.unit_type} · ${aedStr(unit.price_aed)}`, project, unit,
        analysis: computeAnalysis(project, unit, ltrOpts),
        analysisStr: computeAnalysis(project, unit, strOpts),
        hasStr: unitHasStr(unit),
      };
    }).filter(Boolean);
  }, [data]);   // eslint-disable-line
  const compareStrategy = a.strategy || 'ltr';

  // bulk deal
  const bulk = useMemo(() => {
    if (!data || data.mode !== 'bulk' || !data.items?.length) return null;
    const lookup = {};
    for (const it of data.items) {
      const u = it.project.units.find(x => x.id === it.unit_id) || it.project.units[0];
      if (u) lookup[it.unit_id] = { project: it.project, unit: u };
    }
    const deal = a.deal || {};
    const strategy = a.strategy || 'ltr';
    const lines = (deal.items || []).map(di => {
      const f = lookup[di.unit_id];
      if (!f) return null;
      const line = { project: f.project, unit: f.unit, quantity: di.quantity || 1, priceOverride: di.price_override, rentOverride: di.rent_override };
      if (strategy === 'str') {
        line.str = { adr: Number(f.unit.str_adr) || 0, occupancy: Number(f.unit.str_occupancy) || 75 };
        line.furnishingCost = f.unit.furnishing_cost != null ? Number(f.unit.furnishing_cost) : 0;
      }
      return line;
    }).filter(Boolean);
    if (!lines.length) return null;
    const opts = { capBands: a.capBands || [8, 5, 3], rentBands: a.rentBands || [5, 3, 2], maturityYears: a.maturityYears || 10, strategy };
    const b = computeBulkAnalysis(lines, opts, deal.bulk_discount_pct ?? null);
    return { b, name: deal.name || 'Package Purchase' };
  }, [data]);   // eslint-disable-line

  const title = single
    ? `${single.project.project_name} — ${single.unit.unit_type}`
    : bulk ? `${bulk.name} — Bulk Deal`
    : (compareEntries.length ? 'Investment Comparison' : 'Investment Analysis');

  // dynamic page title + link-preview meta
  useEffect(() => {
    if (!data) return;
    const desc = single
      ? `${single.unit.unit_type} investment analysis${single.project.area ? ' in ' + single.project.area : ''} — prepared by ${CONTACT_NAME}, ${CONTACT_COMPANY}.`
      : bulk
      ? `Bulk deal — package of ${bulk.b.units} units, total ${aedStr(bulk.b.price)} — prepared by ${CONTACT_NAME}, ${CONTACT_COMPANY}.`
      : `Side-by-side investment comparison of ${compareEntries.length} units — prepared by ${CONTACT_NAME}, ${CONTACT_COMPANY}.`;
    setMeta(`${title} — Investment Analysis`, desc);
  }, [data, title]);   // eslint-disable-line

  async function downloadA4() {
    if (!reportRef.current) return;
    setPdfBusy(true);
    try {
      // Ensure the Hebrew webfont is fully loaded before rasterising, so the PDF
      // captures real glyphs (no tofu boxes) rather than a fallback.
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
      const name = single
        ? pdfFilename(single.project.project_name, single.unit.unit_type, 'A4')
        : bulk ? pdfFilename('Bulk-Deal', bulk.name || `${bulk.b.units}-units`, 'A4')
        : pdfFilename('Comparison', `${compareEntries.length}-units`, 'A4');
      await exportElementToPdf(reportRef.current, name);
    } catch (e) { /* ignore */ }
    setPdfBusy(false);
  }
  async function downloadMobile() {
    if (!mobileRef.current || !(single || bulk)) return;
    setPdfBusy(true);
    try {
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
      const name = single
        ? pdfFilename(single.project.project_name, single.unit.unit_type, 'Mobile')
        : pdfFilename('Bulk-Deal', bulk.name || `${bulk.b.units}-units`, 'Mobile');
      await exportMobileElementToPdf(mobileRef.current, name);
    } catch (e) { /* ignore */ }
    setPdfBusy(false);
  }

  const waText = `Hi ${CONTACT_NAME}, I'd like to discuss the ${title} analysis you shared.`;

  if (error) return <Centered dir={dir} lang={lang}><p className="text-4xl mb-3">🔒</p><p className="text-gray-700 font-medium">{t(error === 'expired' ? 'link_expired' : 'link_unavailable')}</p><p className="text-gray-400 text-sm mt-1">{t('contact_for_link', { name: CONTACT_NAME })}</p></Centered>;

  return (
    <I18nProvider lang={lang}>
    <div dir={dir} lang={lang} className="min-h-screen bg-stone-50 pb-24 md:pb-0">
      {/* elegant client header */}
      <header className="relative overflow-hidden text-white bg-gradient-to-r from-dark via-dark to-card">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: 'radial-gradient(1200px 200px at 80% -50%, #1D9E75, transparent)' }} />
        <div className="relative max-w-5xl mx-auto px-4 md:px-8 py-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 bg-primary rounded-2xl flex items-center justify-center text-xl shadow-lg shrink-0">🏙️</div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-widest text-primary/80 font-semibold">{t('investment_analysis')}</p>
              <p className="font-bold truncate">{title}</p>
              <p className="text-xs text-gray-300 truncate">{t('prepared_by_x', { name: CONTACT_NAME, company: CONTACT_COMPANY })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* EUR toggle — optional third currency line, off hides every € figure */}
            <button onClick={() => setShowEur(v => !v)} aria-pressed={showEur} title={showEur ? 'Hide EUR conversion' : 'Show EUR conversion'}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition ${showEur ? 'bg-white/20 border-white/20 text-white' : 'bg-white/5 border-white/15 text-white/50 hover:text-white/80'}`}>
              <span className={`relative w-7 h-4 rounded-full transition shrink-0 ${showEur ? 'bg-primary' : 'bg-white/20'}`}>
                <span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition ${showEur ? 'translate-x-3' : ''}`}></span>
              </span>
              €
            </button>
            {/* USD toggle — optional third currency line, off hides every $ figure */}
            <button onClick={() => setShowUsd(v => !v)} aria-pressed={showUsd} title={showUsd ? 'Hide USD conversion' : 'Show USD conversion'}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition ${showUsd ? 'bg-white/20 border-white/20 text-white' : 'bg-white/5 border-white/15 text-white/50 hover:text-white/80'}`}>
              <span className={`relative w-7 h-4 rounded-full transition shrink-0 ${showUsd ? 'bg-primary' : 'bg-white/20'}`}>
                <span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition ${showUsd ? 'translate-x-3' : ''}`}></span>
              </span>
              $
            </button>
            {/* language toggle — EN / עברית (switches the whole report to Hebrew RTL) */}
            <div className="inline-flex bg-white/10 border border-white/15 rounded-lg p-0.5 text-xs">
              <button onClick={() => setLang('en')} aria-pressed={lang === 'en'}
                className={`px-2 py-1 rounded-md font-semibold transition ${lang === 'en' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white/80'}`}>EN</button>
              <button onClick={() => setLang('he')} aria-pressed={lang === 'he'}
                className={`px-2 py-1 rounded-md font-semibold transition ${lang === 'he' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white/80'}`}>עברית</button>
            </div>
            {loading ? (
              <button disabled className="bg-white/10 border border-white/20 px-4 py-2 rounded-xl text-sm font-medium opacity-50">⬇ PDF</button>
            ) : (single || bulk) ? (
              <PdfDownloadButton onA4={downloadA4} onMobile={downloadMobile} busy={pdfBusy} dark />
            ) : (
              <button onClick={downloadA4} disabled={pdfBusy} className="bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-50">
                {pdfBusy ? t('generating') : t('download_pdf')}
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {loading ? (
          <ReportSkeleton />
        ) : (
          <>
          {(single || bulk) && (
            <div className="flex items-center gap-2 mb-3">
              <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
                {[['lite', t('overview')], ['advanced', t('full_detail')]].map(([v, lbl]) => (
                  <button key={v} onClick={() => setView(v)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${view === v ? 'bg-dark text-white' : 'text-gray-500 hover:text-gray-700'}`}>{lbl}</button>
                ))}
              </div>
              <span className="text-[11px] text-gray-400">{view === 'lite' ? t('the_essentials') : t('full_tables')}</span>
            </div>
          )}
          <div ref={reportRef} className="bg-stone-50 p-1">
            {single ? (
              <>
                {single.strategy === 'compare' && single.analysisStr && (
                  <StrVsLtr ltr={single.analysis} str={single.analysisStr} ils={ils} eur={effEur} usd={effUsd} />
                )}
                <AnalysisReport
                  project={single.project} unit={single.unit}
                  analysis={single.strategy === 'ltr' ? single.analysis : single.analysisStr}
                  ils={ils} eur={effEur} usd={effUsd} assumptions={single.assumptions} engineOpts={single.engineOpts} view={view} showSummary presentation
                  strategy={single.strategy === 'ltr' ? 'ltr' : 'str'} resale={resale} setResale={setResale}
                  altAnalysis={single.strategy === 'compare' ? single.analysis : null}
                  exitYear={chatExitYear} onExitYearChange={setChatExitYear} />
                <ReportFooter fixedRate={fixedRate} />
              </>
            ) : bulk ? (
              <>
                <BulkReport bulk={bulk.b} ils={ils} eur={effEur} usd={effUsd} dealName={bulk.name} view={view} presentation resale={resale} setResale={setResale} />
                <ReportFooter fixedRate={fixedRate} />
              </>
            ) : <ComparisonReport entries={compareEntries} ils={ils} eur={effEur} usd={effUsd} strategy={compareStrategy} />}
          </div>

          {/* "Ask about this deal" — deliberately OUTSIDE reportRef/mobileRef so it never
              appears in an exported PDF. Only offered when the presenter enabled it. */}
          {allowQuestions && (
            <AnalysisAssistant
              context={askContext} contextVersion={askContextVersion}
              askUrl={`${API}/share/analysis/${token}/ask`}
              matchInput={{ analysis: single.analysis, analysisStr: single.analysisStr, strategy: single.strategy, ils }}
              onExitYearChange={setChatExitYear} />
          )}
          </>
        )}

        {/* off-screen single-column layout — mounted once data loads so its charts
            are laid out and ready to rasterise for the "PDF — Mobile" export */}
        {!loading && (single || bulk) && (
          <div aria-hidden style={{ position: 'fixed', left: -10000, top: 0, width: 400, pointerEvents: 'none', zIndex: -1 }}>
            <div ref={mobileRef} dir={dir} lang={lang} style={{ width: 400 }}>
              {single ? (
                <MobileReport
                  project={single.project} unit={single.unit}
                  analysis={single.strategy === 'ltr' ? single.analysis : single.analysisStr}
                  ils={ils} eur={effEur} usd={effUsd} assumptions={single.assumptions}
                  strategy={single.strategy === 'ltr' ? 'ltr' : 'str'} resale={resale} fixedRate={fixedRate} />
              ) : (
                <>
                  <BulkReport bulk={bulk.b} ils={ils} eur={effEur} usd={effUsd} dealName={bulk.name} view="lite" presentation resale={resale} />
                  <ReportFooter fixedRate={fixedRate} />
                </>
              )}
            </div>
          </div>
        )}

        {/* interactive CTA (branding + disclaimer are baked into the ReportFooter above) */}
        {!loading && (
          <footer className="mt-8 pb-12 text-center">
            <a href={waLink(waText)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-2xl text-sm font-semibold shadow-sm transition">
              <WhatsAppIcon /> {t('discuss_on_whatsapp')}
            </a>
          </footer>
        )}
      </div>

      {/* sticky bottom bar on mobile — one-tap WhatsApp */}
      {!loading && !error && (
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 px-4 py-3 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-400 leading-none mb-0.5">{t('interested')}</p>
            <p className="text-sm font-semibold text-gray-800 truncate">{t('talk_to_x', { name: CONTACT_NAME })}</p>
          </div>
          <a href={waLink(waText)} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-5 py-2.5 rounded-xl text-sm font-semibold shrink-0 shadow-sm">
            <WhatsAppIcon className="w-4 h-4" /> WhatsApp
          </a>
        </div>
      )}
    </div>
    </I18nProvider>
  );
}

function Centered({ children, dir = 'ltr', lang = 'en' }) {
  return <div dir={dir} lang={lang} className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-center px-6">{children}</div></div>;
}
